import { useEffect } from 'react';
import {
  createRootRouteWithContext,
  Outlet,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { onUnauthorized } from '../lib/http';
import { useAuthGate } from '../features/auth/authGate';
import { ME_KEY } from '../features/auth/useAuth';
import { useStartSync } from '../offline/useSync';
import { Icon } from '../components/Icon';
import { ErrorState } from '../components/States';

export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFound,
  errorComponent: ({ error, reset }) => (
    <div className="lk-shell lk-safe pt-10">
      <ErrorState error={error} onRetry={reset} title="Щось зламалось" />
    </div>
  ),
});

function RootLayout(): React.JSX.Element {
  useStartSync();
  useUnauthorizedRedirect();

  return (
    <div className="min-h-dvh">
      <Outlet />
    </div>
  );
}

/**
 * Єдина точка реакції на 401: чистимо серверний кеш профілю і ведемо на вхід.
 * Офлайн-черга при цьому не чіпається — саме тому вихід не коштує дитині
 * жодної відповіді.
 */
function useUnauthorizedRedirect(): void {
  const navigate = useNavigate();
  const expiredAt = useAuthGate((s) => s.expiredAt);
  const markExpired = useAuthGate((s) => s.markExpired);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const context = Route.useRouteContext();

  useEffect(() => onUnauthorized(() => markExpired()), [markExpired]);

  useEffect(() => {
    if (expiredAt === null) return;
    context.queryClient.setQueryData(ME_KEY, null);
    if (pathname !== '/login') {
      void navigate({ to: '/login', search: { expired: true } });
    }
  }, [expiredAt, pathname, navigate, context.queryClient]);
}

function NotFound(): React.JSX.Element {
  return (
    <div className="lk-shell lk-safe grid min-h-dvh place-items-center text-center">
      <div>
        <Icon name="compass" size={88} className="mx-auto" />
        <h1 className="mt-3 text-2xl font-black">Такої сторінки немає</h1>
        <p className="mt-2 text-sm font-semibold text-white/60">
          Схоже, ми звернули не туди. Повернись на арену.
        </p>
        <a href="/arena" className="lk-btn lk-btn--gold mt-5 inline-flex">
          На арену
        </a>
      </div>
    </div>
  );
}
